import React, { useCallback, useRef, useState } from 'react';
import { BookmarkIcon, CheckIcon, ChevronDownIcon, RotateCcwIcon, SaveIcon, Trash2Icon } from 'lucide-react';
import { Popover } from '../ui/Popover';

export function ViewsMenu({ builtIn, custom, activeView, isDirty, onApply, onSaveNew, onUpdateActive, onDelete }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const close = useCallback(() => setOpen(false), []);

  const save = () => {
    if (!name.trim()) return;
    onSaveNew(name.trim());
    setName('');
    close();
  };

  const item = v => {
    const active = v.id === activeView.id;
    return (
      <li key={v.id} className="tw-group/view tw-flex tw-items-center tw-rounded-md tw-transition-colors tw-duration-100 hover:tw-bg-sand-50">
        <button
          type="button"
          onClick={() => {
            onApply(v);
            close();
          }}
          className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2.5 tw-px-2.5 tw-py-1.5 tw-text-left">
          <CheckIcon className={`tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-brand-600 ${active ? '' : 'tw-invisible'}`} aria-hidden />
          <span className="tw-min-w-0">
            <span className={`tw-block tw-truncate tw-text-sm tw-text-neutral-900 ${active ? 'tw-font-medium' : ''}`}>{v.name}</span>
            {v.description && <span className="tw-block tw-truncate tw-text-xs tw-text-neutral-400">{v.description}</span>}
          </span>
        </button>
        {!v.builtIn && (
          <button
            type="button"
            onClick={() => onDelete(v.id)}
            aria-label={`Delete view ${v.name}`}
            className="tw-mr-1.5 tw-rounded tw-p-1 tw-text-neutral-400 tw-opacity-0 tw-transition-opacity tw-duration-150 hover:tw-text-red-700 focus-visible:tw-opacity-100 group-hover/view:tw-opacity-100">
            <Trash2Icon className="tw-h-3.5 tw-w-3.5" />
          </button>
        )}
      </li>
    );
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="tw-inline-flex tw-h-9 tw-max-w-[260px] tw-shrink-0 tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-900 tw-transition-colors tw-duration-150 hover:tw-bg-sand-50">
        <BookmarkIcon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-brand-600" aria-hidden />
        <span className="tw-truncate">{activeView.name}</span>
        {isDirty && <span className="tw-rounded tw-bg-amber-50 tw-px-1.5 tw-py-px tw-text-[11px] tw-font-medium tw-text-amber-700">Edited</span>}
        <ChevronDownIcon className="tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-70" aria-hidden />
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={close} label="Saved views" width={300}>
        <div className="scrollbar-thin tw-max-h-[340px] tw-overflow-y-auto tw-p-1.5">
          <p className="tw-px-2.5 tw-pb-1 tw-pt-1.5 tw-text-xs tw-font-medium tw-text-neutral-500">Preset views</p>
          <ul>{builtIn.map(item)}</ul>
          {custom.length > 0 && (
            <>
              <p className="tw-px-2.5 tw-pb-1 tw-pt-3 tw-text-xs tw-font-medium tw-text-neutral-500">Your views</p>
              <ul>{custom.map(item)}</ul>
            </>
          )}
        </div>
        {isDirty && (
          <div className="tw-flex tw-items-center tw-gap-3 tw-border-t tw-border-neutral-200 tw-px-3 tw-py-2">
            <button
              type="button"
              onClick={() => {
                onApply(activeView);
                close();
              }}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-neutral-500 hover:tw-text-neutral-900">
              <RotateCcwIcon className="tw-h-3 tw-w-3" aria-hidden />
              Discard changes
            </button>
            {!activeView.builtIn && (
              <button
                type="button"
                onClick={() => {
                  onUpdateActive();
                  close();
                }}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-brand-600 hover:tw-underline">
                <SaveIcon className="tw-h-3 tw-w-3" aria-hidden />
                Update “{activeView.name}”
              </button>
            )}
          </div>
        )}
        <form
          className="tw-flex tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-3"
          onSubmit={e => {
            e.preventDefault();
            save();
          }}>
          <label htmlFor="reports-new-view-name" className="tw-sr-only">
            Name for new view
          </label>
          <input
            id="reports-new-view-name"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={40}
            placeholder="Save current view as…"
            className="tw-h-8 tw-min-w-0 tw-flex-1 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-2.5 tw-text-sm tw-outline-none placeholder:tw-text-neutral-400 focus:tw-border-brand-500 focus:tw-ring-2 focus:tw-ring-brand-500/20"
          />
          <button
            type="submit"
            disabled={!name.trim()}
            className="tw-h-8 tw-shrink-0 tw-rounded-lg tw-bg-brand-600 tw-px-3 tw-text-sm tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-brand-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
            Save
          </button>
        </form>
      </Popover>
    </>
  );
}
